import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { EFileDetail, Transmission } from '@tabservice/tabfile-client';
import { Card, SectionHeader } from '@/components/ui/card';
import { SearchInput } from '@/components/ui/field';
import { EmptyState, TableSkeleton } from '@/components/ui/feedback';
import { Table, TablePagination, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { RecordStatusPill } from '@/features/filings/status-pill';
import { filingRecordsPageQuery } from '@/lib/queries';
import { useDebounced } from '@/lib/use-debounced';
import { formatCount, formatDateTime } from '@/lib/format';

/** Rows per page, matching the portal's tables. */
const PAGE_SIZE = 10;

/**
 * The records of the parent e-file the IRS accepted. A correction has to carry one of these
 * references, so the list is on the page rather than a tab away.
 *
 * Paged and searched by the database. It used to pull four pages in and filter them in the
 * browser, which meant the footer read "Showing 100 of the first 100 accepted records" and a
 * search for a reference on page five found nothing - the answer depended on how much had
 * been fetched rather than on what the e-file holds.
 */
export function AcceptedRecordsPanel({ filingId }: { filingId: string }) {
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(1);
  const search = useDebounced(term.trim());

  // A new search is a new list; staying on page five of the old one shows an empty table.
  useEffect(() => {
    setPage(1);
  }, [search]);

  const records = useQuery(
    filingRecordsPageQuery(filingId, 'accepted', undefined, page, PAGE_SIZE, undefined, search),
  );
  const rows = records.data?.items ?? [];
  const total = records.data?.total ?? rows.length;

  return (
    <Card data-tour="correction-records">
      <SectionHeader
        title="Accepted records on this e-file"
        description="Every record here can be corrected. Copy its reference into the recordRef of the corrected record."
        actions={
          <div className="w-[260px]">
            <SearchInput
              value={term}
              aria-label="Search accepted records"
              placeholder="Search by reference or recipient"
              onChange={(event) => setTerm(event.target.value)}
            />
          </div>
        }
      />

      {records.isPending ? (
        <TableSkeleton rows={5} columns={3} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={search ? 'No records match that search' : 'No accepted records'}
          description={
            search
              ? 'Clear the search to see the accepted records again.'
              : 'The IRS accepted nothing on this e-file, so there is nothing to correct.'
          }
        />
      ) : (
        <>
          <TableWrap>
            <Table>
              <THead>
                <tr>
                  <TH>Reference</TH>
                  <TH>Recipient</TH>
                  <TH>Status</TH>
                </tr>
              </THead>
              <TBody>
                {rows.map((record) => (
                  <TR key={record.recordRef}>
                    <TD className="font-medium">{record.recordRef}</TD>
                    <TD className="max-w-[280px] truncate text-muted-foreground">
                      {record.recipientName ?? 'Name not on file'}
                    </TD>
                    <TD>
                      <RecordStatusPill status={record.status} />
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableWrap>
          <TablePagination
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            busy={records.isFetching}
            onPageChange={setPage}
          />
        </>
      )}
    </Card>
  );
}

/**
 * A transmission the IRS refused. Its status stays rejected even after a
 * replacement has stood in for it, so `replaced` is what says whether it still
 * needs resending.
 */
export function rejectedTransmissionsOf(filing: EFileDetail): Transmission[] {
  return filing.transmissions.filter(
    (transmission) => transmission.status === 'rejected' || transmission.status === 'partially_accepted',
  );
}

/**
 * The IRS replacement window is measured from the rejection, and the API can
 * report the exact deadline. It is optional in the contract, so the panel says
 * the plain thing when it is absent.
 */
function graceDeadlineOf(filing: EFileDetail): string | null {
  const value = (filing as { replacementDeadline?: string | null }).replacementDeadline;
  return typeof value === 'string' && value ? value : null;
}

export function RejectedTransmissionsPanel({ filing }: { filing: EFileDetail }) {
  const rejected = rejectedTransmissionsOf(filing);
  const deadline = graceDeadlineOf(filing);

  return (
    <Card data-tour="replacement-transmissions">
      <SectionHeader
        title="Transmissions the IRS rejected"
        description="A replacement resends the records that were in these transmissions. Nothing else on the e-file is affected."
      />

      {rejected.length === 0 ? (
        <p className="text-base text-muted-foreground">
          No transmission on this filing is marked rejected. Open the e-file to read what the IRS returned.
        </p>
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <tr>
                <TH align="right">Chunk</TH>
                <TH>Receipt id</TH>
                <TH align="right">Records</TH>
                <TH>What the IRS said</TH>
              </tr>
            </THead>
            <TBody>
              {rejected.map((transmission) => (
                <TR key={transmission.chunk} className="h-auto">
                  <TD numeric>{transmission.chunk}</TD>
                  <TD className="text-muted-foreground">{transmission.receiptId ?? 'none issued'}</TD>
                  <TD numeric>{formatCount(transmission.recordCount)}</TD>
                  <TD className="max-w-[420px] py-3 text-gray-3">
                    {transmission.lastError ?? 'No reason was returned.'}
                    {transmission.acknowledgedAt ? (
                      <span className="mt-0.5 block text-xs text-gray-1">
                        Rejected {formatDateTime(transmission.acknowledgedAt)}
                      </span>
                    ) : null}
                    {transmission.replaced ? (
                      <span className="mt-1 block text-base text-muted-foreground">
                        Already replaced
                        {transmission.replacedByEfileId ? ` by ${transmission.replacedByEfileId}` : ''}.
                      </span>
                    ) : null}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </TableWrap>
      )}

      <p className="pt-5 text-base text-muted-foreground">
        {deadline
          ? `A replacement must reach the IRS by ${formatDateTime(deadline)}. After that the records have to be filed again as an original submission.`
          : 'A replacement must reach the IRS within the IRS grace window. After that the records have to be filed again as an original submission.'}
      </p>
    </Card>
  );
}
